import { useEffect, useState } from "react";
import { PowerStationData } from "../../../models/gas";
import { getPowerStationsData } from "../../../Rest_api/RestApi";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { FormatChartData } from "../../../components/recharts/functions";
import { COLOR_CODES } from "../../../common";
import { ChartType } from "../../../models/chart_model";
import RePieChart from "../../../components/recharts/RePieCharts";
import { capitalize, titleCase } from "../../../extensions/extension";
import { SearchBox } from "../../../components/Search";
import { GAS_BAR_GAP, GAS_COLORS, getAvailableMonths } from "./Gas_Const";
import { renderQuarterTick } from "../../../components/recharts/components";

export function PowerStationAnalysis() {
  const [powerStationData, setPowerStationData] = useState<PowerStationData[]>(
    []
  );
  const [consumptionData, setConsumptionData] = useState<PowerStationData[]>(
    []
  );
  const [selectedMonthYear, setSelectedMonthYear] = useState<any[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  const [capacityMonths, setSelectedCapacityMonths] = useState<any>();

  useEffect(() => {
    _handlefetchData();
  }, []);
  useEffect(() => {
    if (selectedMonthYear.length > 0) {
      setPowerStationData(
        consumptionData.filter((item) =>
          selectedMonthYear.map((item) => item.label).includes(item.month_year)
        )
      );
    } else {
      setPowerStationData(consumptionData);
    }
  }, [consumptionData, selectedMonthYear]);

  async function _handlefetchData() {
    const data = (await getPowerStationsData({})).map((item) => {
      return {
        ...item,
        month: item.month.slice(0, 3),
        state__state_name: titleCase(item.state__state_name),
        month_year: item.month + "-" + item.year,
      };
    });
    setConsumptionData(data);
    const availableMonths = Array.from(
      new Set(
        data.map((item) => {
          return item.month_year;
        })
      )
    )
      .sort((a, b) => a.localeCompare(b))
      .filter(
        // value must be unique
        (value, index, self) => self.findIndex((t) => t === value) === index
      );
    setAvailableMonths(availableMonths);
    setSelectedCapacityMonths({
      label: availableMonths[availableMonths.length - 1],
      value: availableMonths[availableMonths.length - 1],
    });
  }

  return (
    <div className="tabContent">
    <div className="gas-page-container">
      <div className="powerstation-container">
        <div className="gas-chartHeading-container">
          <div className="w-40"></div>

          <h2 className="chartHeading">
            Gas Consumption (MMSCMD) and Tentative Generation (MUs) by Generator
          </h2>
          <div className="w-120">
            <SearchBox
              // unique month-year combination
              options={getAvailableMonths(consumptionData)}
              isMany={true}
              placeholder="Apr-2024 to Jul-2024"
              selectedOptions={selectedMonthYear}
              onChange={(selected: any) => {
                setSelectedMonthYear(selected);
              }}
            />
          </div>
        </div>
        <ReMixChart
          xaxisHeight={40}
          barCategoryGap={GAS_BAR_GAP}
          data={
            FormatChartData({
              data: powerStationData,
              keys: ["power_station_name"],
              groupBy: [],
              volumeKey: "tentative_generation_during_the_month_mus",
              aggregationKeys: [
                "rlng_imported_long_term_contratcs",
                "domestic_gas_alloted_mmscmd_total",
                "gas_consumed_supplied_mmscmd_rlng_imported_spot",
                "gas_consumed_supplied_mmscmd_rlng_imported_long_term",
                "gas_consumed_supplied_mmscmd_domestic_total",
              ],
              priceKey: "",
            }).data.sort(
              // sort on power station name
              (a, b) => a.power_station_name.localeCompare(b.power_station_name)
            ) as any
          }
          legends={[
            {
              dataKey: "_rlng_imported_long_term_contratcs",
              name: "RLNG Imported Long Term ",
              type: ChartType.Bar,
              stroke: GAS_COLORS.rlng_imported_long_term_contratcs, // Darker Blue
              legendColor: GAS_COLORS.rlng_imported_long_term_contratcs, // Darker Blue
              stackId: "b",
            },

            {
              dataKey: "_domestic_gas_alloted_mmscmd_total",
              name: "Alloted Domestic Gas ",
              type: ChartType.Bar,
              stroke: GAS_COLORS.domestic_gas_alloted_mmscmd_total, // Darker Orange
              legendColor: GAS_COLORS.domestic_gas_alloted_mmscmd_total, // Darker Orange
              stackId: "b",
            },
            {
              dataKey: "_gas_consumed_supplied_mmscmd_rlng_imported_spot",
              name: "Consumed RLNG Imported Spot ",
              type: ChartType.Bar,
              stroke:
                GAS_COLORS.gas_consumed_supplied_mmscmd_rlng_imported_spot, // Darker Green
              legendColor:
                GAS_COLORS.gas_consumed_supplied_mmscmd_rlng_imported_spot, // Darker Green
              stackId: "a",
            },
            {
              dataKey: "_gas_consumed_supplied_mmscmd_rlng_imported_long_term",
              name: "Consumed RLNG Imported Long Term ",
              type: ChartType.Bar,
              stroke:
                GAS_COLORS.gas_consumed_supplied_mmscmd_rlng_imported_long_term, // Darker Red (Pinkish)
              legendColor:
                GAS_COLORS.gas_consumed_supplied_mmscmd_rlng_imported_long_term, // Darker Red (Pinkish)
              stackId: "a",
            },
            {
              dataKey: "_gas_consumed_supplied_mmscmd_domestic_total",
              name: "Consumed Domestic Gas ",
              type: ChartType.Bar,
              stroke: GAS_COLORS.gas_consumed_supplied_mmscmd_domestic_total, // Darker Purple
              legendColor:
                GAS_COLORS.gas_consumed_supplied_mmscmd_domestic_total, // Darker Purple
              stackId: "a",
            },
            {
              dataKey: "_tentative_generation_during_the_month_mus",
              name: "Tentative Generation (MUs)",
              type: ChartType.Line,
              stroke: GAS_COLORS.tentative_generation_during_the_month_mus, // Darker Gray
              legendColor: GAS_COLORS.tentative_generation_during_the_month_mus, // Darker Gray
              yAxisId: "right",
              stackId: "1",
            },
          ]}
          showSecondYAxis={true}
          secondYAxisLabel="Generation (MUs)"
          brushIndex={{ startIndex: 0, endIndex: 5 }}
          showBrush={powerStationData.length > 5}
          xDataKey="power_station_name"
          yAxisWidth={50}
          secondYAxisWidth={100}
          unit=""
          labelFontSize={12}
          yAxisLabel="Gas (MMSCMD)"
        />
        <div className="stationSnapshot-two-chartContainer">
          <div className="flex flex-col w-full h-full">
            <h2 className="chartHeading mr-4">
              Gas Consumption and Power Generation by State
            </h2>
            <ReMixChart
              chartContainerHeight="100vh"
              labelFontSize={12}
              fontSize={
                // if width is less than 1500 then reduce font size
                window.innerWidth < 1500 ? 10 : 10
              }
              xAxisAngle={-25}
              xAxisTickMargin={12}
              barCategoryGap={GAS_BAR_GAP}
              xaxisHeight={50}
              yAxisWidth={50}
              // xTick={(a: any)=>renderQuarterTick(a,1)}
              data={
                FormatChartData({
                  data: powerStationData,
                  keys: ["state__state_name"],
                  groupBy: [],
                  volumeKey: "tentative_generation_during_the_month_mus",
                  aggregationKeys: ["gas_consumed_supplied_mmscmd_total"],

                  priceKey: "",
                }).data.sort(
                  // sort on state name
                  (a, b) =>
                    a.state__state_name.localeCompare(b.state__state_name)
                ) as any
              }
              xDataKey="state__state_name"
              unit=""
              legends={[
                {
                  dataKey: "_gas_consumed_supplied_mmscmd_total",
                  type: ChartType.Bar,
                  name: "Gas Consumed Total",
                  legendColor: COLOR_CODES.primary,
                  stroke: COLOR_CODES.primary,
                  legendName: "Gas Consumed Total",
                },
                {
                  dataKey: "_tentative_generation_during_the_month_mus",
                  type: ChartType.Line,
                  yAxisId: "right",

                  name: "Tentative Generation (MUs)",
                  legendColor:
                    GAS_COLORS.tentative_generation_during_the_month_mus, // Darker Gray
                  stroke: GAS_COLORS.tentative_generation_during_the_month_mus, // Darker Gray

                  legendName: "Tentative Generation During the Month (MUs)",
                },
              ]}
              secondYAxisWidth={50}
              secondYAxisLabel="Generation (MUs)"
              yAxisLabel="Gas (MMSCMD)"
            />
          </div>
          <div className="flex flex-col w-full md:w-1/2">
            <div className="flex justify-between items-center	">
              {" "}
              <h2 className="chartHeading">Installed Capacity by Sector</h2>
              <SearchBox
                options={availableMonths.map((item) => {
                  return {
                    label: item,
                    value: item,
                  };
                })}
                selectedOptions={[capacityMonths]}
                isMany={false}
                placeholder=""
                onChange={(selected: any) => {
                  setSelectedCapacityMonths(selected);
                }}
              />
            </div>
            <RePieChart
              data={
                FormatChartData({
                  data: consumptionData.filter(
                    (v) => v.month_year === capacityMonths.value
                  ),
                  keys: ["sector__name"],
                  groupBy: [],
                  volumeKey: "installed_capacity_mw",
                  aggregationKeys: [],
                  priceKey: "",
                }).data as any
              }
              dataKey="_installed_capacity_mw"
              nameKey="sector__name"
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
